import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Close, Done, ExpandMore, InfoOutlined, PowerSettingsNew, ReportOutlined, ViewColumnOutlined } from '@mui/icons-material';
import { useState } from 'react';
import CustomDataGrid from 'components/customDataGrid';
import { useBizTypeTranslation } from 'hooks/useBizTypeTranslation';
import { TYPE_FIELDS, supportsCommands, tableRow } from './tableData';
import ColumnSettings from './ColumnSettings';

const STORAGE_KEY = 'monitoringColumnVisibility';

export default function Tables({ list, onCommand, onFault, onInfo }) {
  const { bt } = useBizTypeTranslation();
  const [settingsType, setSettingsType] = useState(null);
  const [savedVisibility, setSavedVisibility] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
    } catch { return {}; }
  });
  const groups = new Map();
  list.forEach(mc => {
    if (!groups.has(mc.softwareType)) groups.set(mc.softwareType, []);
    groups.get(mc.softwareType).push(mc);
  });
  return <Stack spacing={2} sx={{ width: '100%', minWidth: 0 }}>
    {[...groups].map(([type, controllers]) => {
      const labels = {
        sensor1: ['B1.0', 'B1.6'].includes(type) ? 'RETURN_TEMP' : type === 'B1.9' ? 'RETURN_WATER_TEMP' : 'ACTIVE_TEMP',
        sensor2: type === 'B1.9' ? 'SUPPLY_WATER_TEMP' : 'EXIT_TEMP',
        humidity: 'ACTIVE_DHT', delta: 'DELTA', effective: 'EFFECTIVE', min: 'MIN_TEMP', max: 'MAX_TEMP',
        mode: 'MODE_ACTIVE', setPoint: type === 'B1.8' ? 'SET_POINT_DHT_ACTIVE' : 'SET_POINT_TEMP_ACTIVE', parentName: 'MONITORING_PARENT',
      };
      const units = { sensor1: '°C', sensor2: '°C', min: '°C', max: '°C', delta: '°C', humidity: '%', effective: '%', setPoint: type === 'B1.8' ? '%' : '°C' };
      const columns = [
        { field: 'name', headerName: bt('NAME'), width: 180 },
        { field: 'areaName', headerName: bt('AREA'), width: 140 },
        { field: 'device', headerName: bt('DEVICE_TYPE'), width: 150 },
        { field: 'connection', headerName: bt('CONNECTED_STATUS'), width: 100,
          renderCell: ({ row }) => {
            const connected = row.isConnected && !(row.isB22Sensor &&
              ((row.faults || [row.fault]).includes('SENSOR_DISCONNECTED') || !Number.isFinite(row.temperature)));
            return <Tooltip title={row.connection}><Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              {connected ? <Done role="img" aria-label={row.connection} sx={{ color: 'success.main' }} />
                : <Close role="img" aria-label={row.connection} sx={{ color: '#b71c1c' }} />}
            </Box></Tooltip>;
          } },
        ...(controllers.some(supportsCommands) ? [{ field: 'power', headerName: bt('STATUS'), width: 110 }] : []),
        ...(TYPE_FIELDS[type] || []).map(field => ({ field, headerName: `${bt(labels[field])}${units[field] ? ` (${units[field]})` : ''}`, width: field === 'parentName' ? 180 : 140,
          renderCell: ({ row }) => row[field] ?? '' })),
        { field: 'faultText', headerName: bt('FAULTS'), width: 220 },
        { field: 'actions', headerName: bt('DETAILS'), width: 150, filterable: false, hideInExport: true,
          renderCell: ({ row }) => <Stack direction="row">
            {supportsCommands(row) && <Tooltip title={bt('MONITORING_COMMANDS')}><IconButton aria-label={bt('MONITORING_COMMANDS')} onClick={event => { event.stopPropagation(); onCommand(row); }}><PowerSettingsNew color={row.isConnected && row.turnOnDate ? 'success' : 'error'} /></IconButton></Tooltip>}
            {(row.faults || [row.fault]).filter(Boolean).map(fault => <Tooltip key={fault} title={bt(fault)}><IconButton aria-label={bt(fault)} onClick={event => { event.stopPropagation(); onFault(fault); }}><ReportOutlined color="error" /></IconButton></Tooltip>)}
            {onInfo && !row.isB22Sensor && <IconButton aria-label={bt('DETAILS')} onClick={event => { event.stopPropagation(); onInfo(row.guid); }}><InfoOutlined /></IconButton>}
          </Stack> },
      ];
      // Only currently available columns are configurable; saved settings never add capabilities.
      const visibility = Object.fromEntries(columns.map(column => [column.field,
        column.field === 'name' || savedVisibility[type]?.[column.field] !== false]));
      return <Box key={type} sx={{ position: 'relative', minWidth: 0, width: '100%' }}><Accordion defaultExpanded disableGutters sx={{ minWidth: 0, width: '100%', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ '& .MuiAccordionSummary-content': { paddingInlineEnd: '48px' } }}><Typography>{type} ({controllers.length})</Typography></AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 0.5, sm: 2 }, minWidth: 0 }}>
          <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}><CustomDataGrid columns={columns} columnVisibilityModel={visibility}
            list={controllers.map(mc => tableRow(mc, bt))} pageSize={12} disableRowSelectionOnClick
            getRowClassName={({ row }) => row.fault || !row.isConnected ? 'hot' : ''}
            setItem={row => { if (supportsCommands(row)) onCommand(row); }} /></Box>
        </AccordionDetails>
        </Accordion>
        <Tooltip title={bt('DISPLAY_SETTINGS')}><IconButton aria-label={`${bt('DISPLAY_SETTINGS')} ${type}`}
          sx={{ position: 'absolute', top: 4, insetInlineEnd: 48, zIndex: 1 }}
          onClick={() => setSettingsType(type)}><ViewColumnOutlined /></IconButton></Tooltip>
        {settingsType === type && <ColumnSettings type={type} columns={columns} visibility={visibility}
          onClose={() => setSettingsType(null)} onSave={draft => {
            const next = { ...savedVisibility, [type]: { ...draft, name: true } };
            setSavedVisibility(next);
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* Keep the current session usable. */ }
          }} />}
      </Box>;
    })}
  </Stack>;
}
